import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { runInAction } from 'mobx';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Chip,
  CssBaseline,
  Divider,
  Drawer,
  LinearProgress,
  Snackbar,
  SnackbarContent,
  FormControl,
  Grid,
  Hidden,
  IconButton,
  InputBase,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Toolbar,
  Typography,
  CircularProgress,
  FormControlLabel,
  Switch,
  Popover,
  ListItemIcon,
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import FilterListIcon from '@material-ui/icons/FilterList';
import EmojiEmotionsOutlinedIcon from '@material-ui/icons/EmojiEmotionsOutlined';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import SendIcon from '@material-ui/icons/Send';
import { Close } from '@material-ui/icons';
import OptionsMenu from './OptionsMenu';
import WhatsAppStatusChip from './WhatsAppStatusChip';
import BroadcastDialog from './BroadcastDialog';
import moment from 'moment';
import { observer } from 'mobx-react-lite';
import EmojiPicker from 'emoji-picker-react';
import { ChevronUp, ChevronDown, GroupIcon, DeleteIcon, TrashIcon } from 'lucide-react';
import MessageBubble from './partials/MessageBubble';
import StatusIcon from './partials/StatusIcon';
import { TapTooltip } from '../partials/TapTooltip';
import { usePermissions } from '../../utils/PermissionProvider';
import useConflictConnectionDialog from './ConflictConnectionDialog';
import useConfirmDialog from '../ConfirmDialog';
import { getWaStatus } from '../../utils/whatsapp';
import PremiumNoticeModal from './partials/premiumNoticeModal';
import { BAILEY_PERMISSION, BASIC_WHATSAPP_PERMISSION } from '../../constants/permission';
import useTranslation from "next-translate/useTranslation";

const useStyles = makeStyles((theme) => ({
  root: { background: '#efeae2' },
  shell: { height: '85vh', margin: '0 auto' },
  sidebar: {
    display: 'grid',
    gridTemplateRows: 'auto auto 1fr',
    height: '85vh',
    overflowY: "auto",
    background: '#fff',
    borderRight: '1px solid #e0e0e0',
    minWidth: 0,
  },
  contactsScroll: {
    minHeight: 0,
    overflowY: 'auto',
    minWidth: 0,
  },
  sideHeader: {
    minHeight: 56,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #eee',
    gap: 12,
    minWidth: 0,
  },
  propertySelect: {
    minWidth: 180,
    maxWidth: '100%',
    '& .MuiSelect-select': { paddingTop: 8, paddingBottom: 8 },
  },
  searchWrap: {
    padding: theme.spacing(1, 2),
    borderBottom: '1px solid #f0f0f0',
    minWidth: 0,
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    background: '#f5f6f6',
    borderRadius: 16,
    padding: theme.spacing(0.5, 1),
  },
  searchMessage: {
    display: 'flex',
    alignItems: 'center',
    background: '#ffffffff',
    borderRadius: 16,
    padding: theme.spacing(0.5, 1),
  },
  chatWrap: { 
    height: '85vh', 
    overflowY: 'auto', 
    display: 'flex', 
    flexDirection: 'column', 
    background: '#f0f2f5' 
  },
  chatHeader: { borderBottom: '1px solid #e0e0e0', background: '#f0f2f5' },
  titleRow: { display: 'flex', alignItems: 'center', gap: 12 },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(3),
    backgroundImage:
      'radial-gradient(#dadada 1px, transparent 1px), radial-gradient(#dadada 1px, transparent 1px)',
    backgroundSize: '28px 28px',
    backgroundPosition: '0 0, 14px 14px',
  },
  dayDivider: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: 8,
    background: '#e2e6e9',
    color: '#54656f',
    fontSize: 12,
    margin: theme.spacing(2, 0),
    alignSelf: 'center',
  },
  inputBar: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    gap: 8,
    padding: theme.spacing(1),
    background: '#f0f2f5',
    borderTop: '1px solid #e0e0e0',
  },
  input: {
    flex: 1,
    background: '#fff',
    borderRadius: 24,
    padding: theme.spacing(1, 2),
    boxShadow: '0 1px 0 rgba(0,0,0,0.06)',
  },
  banner: {
    background: '#fff8e1',
    border: '1px solid #ffe08a',
    color: '#7a5b00',
    padding: '6px 10px',
    borderRadius: 8,
    margin: '10px 16px 0',
  },
  previewMedia: {
    width: '100%',
    maxHeight: 420,
    borderRadius: 12,
    objectFit: 'contain',
    background: '#000',
  },
  connectingToast: {
    backgroundColor: '#ffffff',
    color: '#0f172a',
    border: '1px solid #e5e7eb',
    boxShadow: '0 10px 15px rgba(0,0,0,0.08)',
    padding: theme.spacing(1),
    minWidth: 320,
  },
  connectingTitle: {
    fontWeight: 700,
    fontSize: 14,
    marginBottom: 4,
  },
  connectingSub: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 8,
  },
  duplicateIndicator: {
    backgroundColor: '#ffecb3',
    color: '#e65100',
    fontSize: 9,
    height: 18,
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: '#ffe082'
    }
  },
  duplicateAvatar: {
    border: '2px solid #ff5722',
    backgroundColor: '#ff9800'
  },
  duplicateRoomTag: {
    marginLeft: 'auto',
    backgroundColor: '#f5f5f5',
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 10
  },
  duplicateWarningHeader: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    fontSize: 10,
    height: 20,
    fontWeight: 'bold'
  }
}));

function DayLabel({ label }) {
  const classes = useStyles();
  return <Box className={classes.dayDivider}>{label}</Box>;
}

export default observer(function ChatWhatsAppLike({
  properties = [],
  selectedProperty,
  onSelectProperty,
  session,
  usingFallback,
  onChangeUsingFallback,
  onAfterSend,
  onOpenScan,
  store,
  chatIdNotification = "",
  propertyIdNotification = "",
}) {
  const classes = useStyles();
  const { hasPermission } = usePermissions();
  const { t } = useTranslation("common");
  
  // ===== State Management =====
  const [loadingChats, setLoadingChats] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeChat, setActiveChat] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [showConnecting, setShowConnecting] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUnanswered, setFilterUnanswered] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [ConflictDialog, openConflictDialog] = useConflictConnectionDialog();
  const [showBanner, setShowBanner] = useState(true);
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  
  // Message search state
  const [searchMessage, setSearchMessage] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchIndex, setSearchIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  // Tenants state
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  
  // Broadcast state
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastLists, setBroadcastLists] = useState([]);
  const [broadcastDetailsOpen, setBroadcastDetailsOpen] = useState(false);
  const [broadcastIdToDelete, setBroadcastIdToDelete] = useState(null);
  const [BroadcastDeleteConfirmDialog, setBroadcastDeleteConfirmDialog] = useConfirmDialog();
  
  // Media preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pendingMedia, setPendingMedia] = useState(null);
  
  // Refs
  const messagesRef = useRef(null);
  const msgEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const searchTimeout = useRef(null);

  // ===== Computed Properties =====
  const propertyId = selectedProperty?._id || '';
  const property = selectedProperty || {};
  const phoneNumber = selectedProperty?.phone || null;
  const isSuperkos = usingFallback;
  const status = getWaStatus(session?.status || 'disconnected');

  
  // ===== Helper Functions =====
  const scrollToBottom = useCallback((immediate = true) => {
    const el = messagesRef.current;
    if (!el) return;
    
    if (immediate) {
      el.scrollTop = el.scrollHeight;
    } else {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, []);

  const fmtTimeID = useCallback((ts) =>
    new Intl.DateTimeFormat("id-ID", {
      timeZone: "Asia/Jakarta",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(ts)), []);

  const toDateKey = useCallback((iso) =>
    new Intl.DateTimeFormat("sv-SE", { 
      timeZone: "Asia/Jakarta" 
    }).format(new Date(iso)), []);

  const getMsgId = useCallback((m, idx) => {
    if (!m) return `msg-fallback-${idx}`;
    const id = m.id || m.messageId || m.whatsappMessageId || 
              (m.key && (m.key.id || m.key.remoteJid)) ||
              String(m.timestamp || m.date || '');
    const safe = String(id).replace(/[^a-zA-Z0-9-_:.]/g, '');
    return safe || `msg-${idx}`;
  }, []);

  const parseCompositeId = useCallback((compositeId) => {
    if (!compositeId || typeof compositeId !== 'string') return { chatId: null, tenantId: null };
    
    if (compositeId.includes('::')) {
      const [chatId, tenantId] = compositeId.split('::');
      return { chatId, tenantId };
    }
    
    // Fallback for old format or broadcast IDs
    return { chatId: compositeId, tenantId: null };
  }, []);

  const formatLastMessageAt = useCallback((ts) => {
    if (!ts) return '';
    const m = moment(ts);
    if (!m.isValid()) return '';

    if (m.isSame(moment(), 'day')) return m.format('HH:mm');
    if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';

    return m.locale('id').format('ll');
  }, []);

  // Get chats from store using new API
  const chats = store.whatsapp.getChats(propertyId, isSuperkos);

  const getMessages = useCallback(() => {
    if (!activeChat || !propertyId) return [];
    
    if (store.whatsapp.isBroadcastChat(activeChat)) {
      const listId = activeChat.replace('broadcast_', '');
      
      // This ensures proper MobX reactivity tracking
      const messages = store.whatsapp.getBroadcastMessages(listId, propertyId, isSuperkos);
      
      return messages;
    }

    const { chatId } = parseCompositeId(activeChat);
    if (!chatId) return [];
    
    return store.whatsapp.getMessages(chatId, propertyId, isSuperkos);
  }, [activeChat, propertyId, isSuperkos, store.whatsapp]);

  // Get messages using new store API
  const messages = getMessages();

  // ===== Early action =====
  useEffect(() => {
    const initializePermissions = async () => {
      if (!store.rbac) {
        return;
      }

      // Check if permissions are already loaded
      if (store.rbac.permissions && store.rbac.permissions.length > 0) {
        return;
      }
      
      try {
        await store.rbac.getPermissions(true); // Force refresh
      } catch (error) {
        // console.error('Failed to load RBAC permissions:', error);
      }
    };

    initializePermissions();
  }, [store.rbac]);

  useEffect(() => {
    try {
      if (store && store.whatsapp) {
        // If the store's value differs from the prop, inform the parent via the
        // onChangeUsingFallback callback so the page can react accordingly.
        if (typeof onChangeUsingFallback === 'function' && store.whatsapp.usingFallback !== usingFallback) {
          onChangeUsingFallback(Boolean(store.whatsapp.usingFallback));
        }
      }
    } catch (err) {
      // ignore
    }
  }, [store?.whatsapp?.usingFallback, usingFallback, onChangeUsingFallback, store]);

  // ===== Data Loading Effects =====

  // ===== Chat Selection and Message Loading =====
  useEffect(() => {
    if (!activeChat || !propertyId) {
      setSearchMessage('');
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Handle broadcast chat
    if (store.whatsapp.isBroadcastChat(activeChat)) {
      store.whatsapp.loadBroadcastMessagesIfNeeded(activeChat, propertyId, isSuperkos);
      setTimeout(() => scrollToBottom(true), 200);
      return;
    }

    // Parse composite ID to get original chatId
    const { chatId } = parseCompositeId(activeChat);
    if (!chatId) return;

    // Check if messages already loaded
    const isLoading = store.whatsapp.isLoading(chatId, propertyId, isSuperkos);
    const existingMessages = store.whatsapp.getMessages(chatId, propertyId, isSuperkos);

    if (existingMessages.length > 0 && !isLoading) {
      requestAnimationFrame(() => scrollToBottom(true));
      return;
    }

    if (isLoading) {
      return;
    }

    // Fetch messages
    const fetchMessages = isSuperkos 
      ? store.whatsapp.fetchChatMessagesSuperkos 
      : store.whatsapp.fetchChatMessages;
    
    if (fetchMessages) {
      fetchMessages.call(store.whatsapp, propertyId, chatId)
        .catch(error => {
          // console.error('Failed to fetch messages:', error);
        });
    }

    requestAnimationFrame(() => scrollToBottom(true));
  }, [activeChat, propertyId, isSuperkos, store.whatsapp]);

  // ===== Auto-scroll when messages change =====
  useEffect(() => {
    if (messages.length > 0 && !isSearching) {
      // Use a shorter delay for better responsiveness
      const timeoutId = setTimeout(() => {
        scrollToBottom(true);
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [messages, isSearching, scrollToBottom]);

  // ===== Connection Status =====
  useEffect(() => {
    const syncing = store.whatsapp.historySync?.syncing;
    const syncStatus = store.whatsapp.historySync?.status;
    
    if (syncing && (syncStatus === 'started' || status === "connecting")) {
      setShowConnecting(true);
    } else {
      setShowConnecting(false);
    }
  }, [store.whatsapp.historySync?.syncing, store.whatsapp.historySync?.status, status]);

  // ===== Notification Handling =====
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().then((perm) => {
        console.log('Notification permission:', perm);
      });
    }

    const handleWindowMessage = (e) => {
      if (!e?.data?.__wa_open_chat) return;
      
      const { chatId, propertyId: propId } = e.data;
      
      if (propId && propId !== propertyId) {
        onSelectProperty?.(propId);
      }
      if (chatId) {
        setActiveChat(chatId);
      }
      requestAnimationFrame(() => scrollToBottom(true));
    };

    window.addEventListener('message', handleWindowMessage);
    return () => window.removeEventListener('message', handleWindowMessage);
  }, [propertyId, onSelectProperty]);

  // ===== Message Grouping =====
  const perDay = useMemo(() => {
    // Sort messages by timestamp first (oldest to newest for proper chat flow)
    const sortedMessages = messages.slice().sort((a, b) => {
      const timeA = new Date(a?.timestamp || a?.date || 0).getTime();
      const timeB = new Date(b?.timestamp || b?.date || 0).getTime();
      return timeA - timeB; // Oldest first for chat display
    });

    return sortedMessages.reduce((groups, m) => {
      const iso = m?.timestamp || m?.date;
      if (!iso) return groups;
      const key = toDateKey(iso);
      (groups[key] = groups[key] || []).push(m);
      return groups;
    }, {});
  }, [messages, toDateKey]);

  const sortedDays = useMemo(() => {
    return Object.keys(perDay).sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime(); // Oldest day first
    });
  }, [perDay]);

  // ===== Contact Processing =====
  const contacts = useMemo(() => {
    const toChatId = (phone) => {
      if (!phone) return null;
      const digits = String(phone).replace(/[^\d]/g, '');
      return `${digits}@s.whatsapp.net`;
    };

    const list = Array.isArray(chats) ? chats : [];

    // First pass - detect duplicate phone numbers
    const phoneToTenants = new Map();
    (Array.isArray(tenants) ? tenants : []).forEach((tenant) => {
      (tenant.contacts || []).forEach((contact) => {
        if (contact?.phone) {
          const normalizedPhone = String(contact.phone).replace(/[^\d]/g, '');
          if (!phoneToTenants.has(normalizedPhone)) {
            phoneToTenants.set(normalizedPhone, []);
          }
          phoneToTenants.get(normalizedPhone).push(tenant);
        }
      });
    });

    // ===== RESTORE ORIGINAL TENANT-CHAT MERGING LOGIC =====
    const mergedList = (Array.isArray(tenants) ? tenants : []).flatMap((tenant) => {
      const contactsArr = tenant.contacts || [];
      if (contactsArr.length === 0) {
        return [];
      }

      return contactsArr.map((contact, index) => {
        const contactChatId = toChatId(contact?.phone);
        const matchingChat = list.find((c) => c.chatId === contactChatId);

        const normalizedPhone = contact?.phone ? String(contact.phone).replace(/[^\d]/g, '') : null;
        const duplicateTenants = normalizedPhone ? phoneToTenants.get(normalizedPhone) || [] : [];
        const hasDuplicates = duplicateTenants.length > 1;

        const displayName = contactsArr.length > 1 
          ? (contact.name ? `${tenant.name} - (${contact.name})` : `${tenant.name} - (${index + 1})`)
          : tenant.name;

        return {
          ...tenant,
          name: displayName,
          chatData: matchingChat || null,
          hasDuplicatePhone: hasDuplicates,
          duplicateCount: duplicateTenants.length,
          duplicateTenants: duplicateTenants,
          currentContact: contact,
        };
      });
    });

    // ===== MAP MERGED LIST TO CONTACT FORMAT =====
    const mapped = (mergedList || []).map((c) => {
      const currentContact = c.currentContact || c.contacts?.[0] || {};
      const phone = currentContact.phone?.replace(/\+/g, '') || '';
      const chatId = c.chatData?.chatId || toChatId(phone);
      
      // Create composite ID that includes tenant ID
      const compositeId = `${chatId}::${c._id}`;

      // Enhanced name with duplicate indicator
      let displayName = c.name || c.chatData?.name || c.chatData?.chatId || 'Unknown';

      return {
        id: compositeId,
        chatId: chatId,
        lid: c.chatData?.chatLid || null,
        name: displayName,
        phone: currentContact.phone || 'Unknown',
        roomName: c.room?.name || 'No Room',
        fromMe: c.chatData?.fromMe || false,
        last: c.chatData?.last || '',
        lastMessage: c.chatData?.lastMessage || '',
        unread: 0,
        status: c.chatData?.status,
        propertyId: c.property?.propertyId || c.propertyId,
        tenantId: c._id,
        messageId: c.chatData?.messageId,
        isGroup: c.chatData?.isGroup,
        lastMessageAt: c.chatData?.lastMessageAt
          ? formatLastMessageAt(c.chatData.lastMessageAt)
          : '',
        lastMessageTimestamp: c.chatData?.lastMessageAt
          ? new Date(c.chatData.lastMessageAt).getTime()
          : 0,
        lastMessageStatus: c.chatData?.lastMessageStatus,
        // Add duplicate flags to contact object
        hasDuplicatePhone: c.hasDuplicatePhone || false,
        duplicateCount: c.duplicateCount || 1,
        duplicateTenants: c.duplicateTenants || [],
        isDuplicatePhone: c.hasDuplicatePhone && c.duplicateCount > 1,
        chatData: c.chatData || null,
      };
    });

    return mapped;
  }, [chats, tenants, isSuperkos, selectedProperty?._id, store.whatsapp.chatsByPropertyId.get(store.whatsapp._generatePropertyKey(propertyId, false))?.lastUpdated, formatLastMessageAt]);

  useEffect(() => {
    let alive = true;
    
    const loadTenantsAndBroadcasts = async () => {
      if (!propertyId) return;
      
      setLoadingChats(true);
      setTenantsLoading(true);
      
      try {
        // Load tenants with the original logic
        const resp = await store.tenant.fetch({ property: propertyId });
        const rawTenants = resp?.data || resp || [];
        const processedTenants = Array.isArray(rawTenants) ? rawTenants : [];
        
        if (!alive) return;
        
        setTenants(processedTenants);
        
        // Load broadcast lists
        const lists = store.whatsapp.getBroadcastLists(propertyId);
        setBroadcastLists(lists);
        
        if (chatIdNotification && propertyIdNotification) {
          setActiveChat(chatIdNotification);
          onSelectProperty(propertyIdNotification);
        }
        
      } catch (error) {
        if (alive) setTenants([]);
      } finally {
        if (alive) {
          setTenantsLoading(false);
          setLoadingChats(false);
        }
      }
    };

    loadTenantsAndBroadcasts();
    return () => { alive = false; };
  }, [propertyId, isSuperkos, store.tenant, store.whatsapp]);
  
  // ===== RESTORE ORIGINAL PHONE/NAME MAPPING LOGIC =====
  const normalizePhone = (v) => String(v || '').replace(/[^\d]/g, '');

  const phoneNameMap = useMemo(() => {
    const m = new Map();
    (contacts || []).forEach((c) => {
      const num = normalizePhone(c?.phone);
      if (!num) return;
      const baseName = c?.name || 'Unknown';
      const withRoom = c?.roomName ? `${baseName} (${c.roomName})` : baseName;
      // One number, appear once
      if (!m.has(num)) m.set(num, withRoom);
    });
    return m;
  }, [contacts]);

  const allPhones = useMemo(() => Array.from(phoneNameMap.keys()), [phoneNameMap]);

  // ===== RESTORE ORIGINAL getRecipientNumber LOGIC =====
  
  const contactsWithBroadcasts = useMemo(() => {
    const broadcastContacts = broadcastLists.map(list => ({
      id: `broadcast_${list.id}`,
      name: list.name,
      phone: `${list.phones?.length || 0} kontak`,
      roomName: 'Broadcast List',
      lastMessage: list.lastMessage || 'Tap untuk kirim pesan',
      lastMessageAt: list.lastMessageAt 
        ? formatLastMessageAt(list.lastMessageAt) 
        : '',
      lastMessageTimestamp: list.lastMessageAt
        ? new Date(list.lastMessageAt).getTime()
        : 0,
      lastMessageStatus: list.lastMessageStatus,
      isBroadcast: true,
      broadcastList: list,
      unread: 0,
    }));

    const allContacts = [...broadcastContacts, ...contacts];

    // Sort by lastMessageTimestamp (most recent first)
    return allContacts.sort((a, b) => {
      const timestampA = a.lastMessageTimestamp || 0;
      const timestampB = b.lastMessageTimestamp || 0;
      
      // If both have timestamps, sort by timestamp (newest first)
      if (timestampA && timestampB) {
        return timestampB - timestampA;
      }
      
      // If only one has timestamp, prioritize it
      if (timestampA && !timestampB) return -1;
      if (!timestampA && timestampB) return 1;
      
      // If neither has timestamp, sort alphabetically
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [broadcastLists, contacts, formatLastMessageAt]);

  const filteredContacts = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    return contactsWithBroadcasts.filter((c) => {
      const matchesSearch = !searchTerm.trim() || (
        (c.name && c.name.toLowerCase().includes(lower)) ||
        (c.roomName && c.roomName.toLowerCase().includes(lower)) ||
        (c.phone && c.phone.toLowerCase().includes(lower))
      );

      const matchesUnanswered = !filterUnanswered || c.status === 'unanswered';

      return matchesSearch && matchesUnanswered;
    });
  }, [contactsWithBroadcasts, searchTerm, filterUnanswered]);

  // Count unanswered chats for the currently selected property
  const unansweredCount = useMemo(() => {
    if (!propertyId) return 0;
    return (contactsWithBroadcasts || []).filter(c => String(c.propertyId || '') === String(propertyId) && c.status === 'unanswered').length;
  }, [contactsWithBroadcasts, propertyId]);

  const selectedContact = useMemo(
    () => contactsWithBroadcasts.find(c => c.id === activeChat),
    [contactsWithBroadcasts, activeChat]
  );

  const getRecipientNumber = useCallback(() => {
    if (store.whatsapp.isBroadcastChat(activeChat)) return null;

    // Parse composite ID to get original chatId
    const { chatId } = parseCompositeId(activeChat);
    if (!chatId) return null;
    
    // Try to get from contact data first
    const contact = contacts?.find(c => c.originalChatId === chatId || c.id === activeChat);
    if (contact?.phone) {
      return String(contact.phone)
        .replace("@s.whatsapp.net", "")
        .replace(/[^\d]/g, "");
    }
    
    // Fallback to parsing activeChat
    return String(chatId || "")
      .replace("@s.whatsapp.net", "")
      .replace(/[^\d]/g, "");
  }, [activeChat, contacts, store.whatsapp]);

  // ===== Event Handlers =====
  const handleChatSelect = useCallback((contact) => {
    setActiveChat(contact.id);
    setSearchResults([]);
    setIsSearching(false);
    setSearchMessage('');
  }, []);

  const toggleEmojiPicker = useCallback(() => {
    setEmojiPickerOpen(prev => !prev);
  }, []);

  const onPickFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Update the handleRefresh function to only handle socket operations
  const handleRefresh = useCallback(async (source = 'manual') => {
    try {
      
      if (isSuperkos) {
        store.pushToastMessage?.({
          message: 'Superkos mode - no socket refresh needed',
          severity: 'info',
        });
        return true;
      }

      if (!propertyId || !phoneNumber) {
        return false;
      }

      // Check if this is a conflict resolution
      if (source === 'conflict-resolution') {
        return await store.whatsapp.resolveConnectionConflict(propertyId);
      }
      
      // Step 1: Clean up existing socket connection
      const oldSocket = store.whatsapp._socketsByProperty?.get(propertyId);
      if (oldSocket) {
        oldSocket.disconnect();
        store.whatsapp._socketsByProperty?.delete(propertyId);
      }
      
      // Clear connection states
      store.whatsapp._connectionStates?.set(propertyId, 'disconnected');
      store.whatsapp._subscriptionStates?.set(propertyId, false);
      
      // Step 2: Create new socket connection
      const socket = await store.whatsapp.ensureSocket(propertyId, isSuperkos);
      
      if (!socket) {
        throw new Error('Failed to create socket connection');
      }
      
      // Step 3: Start fresh session on backend
      await store.whatsapp.startSession(propertyId, isSuperkos);
      
      // Step 4: Subscribe to the socket
      await store.whatsapp.subscribeSocket(propertyId, phoneNumber, isSuperkos);
      
      // Step 5: Wait for subscription to be established
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Step 6: Verify connection state
      const finalState = store.whatsapp.getConnectionState(propertyId);
      const isSubscribed = store.whatsapp._subscriptionStates?.get(propertyId);
      
      if (finalState === 'connected' && isSubscribed) {
        store.pushToastMessage?.({
          message: 'Connection re-established successfully',
          severity: 'success',
        });
        return true;
      } else {
        throw new Error(`Connection failed - State: ${finalState}, Subscribed: ${isSubscribed}`);
      }
      
    } catch (err) {
      // Show error message to user
      store.pushToastMessage?.({
        message: `Failed to refresh connection: ${err.message || err}`,
        severity: 'error',
      });
      
      // Clean up failed connection attempt
      try {
        const failedSocket = store.whatsapp._socketsByProperty?.get(propertyId);
        if (failedSocket) {
          failedSocket.disconnect();
          store.whatsapp._socketsByProperty?.delete(propertyId);
        }
        store.whatsapp._connectionStates?.set(propertyId, 'disconnected');
        store.whatsapp._subscriptionStates?.set(propertyId, false);
      } catch (cleanupErr) {
        // console.error(`[${source} socket refresh] Cleanup failed:`, cleanupErr);
      }
      
      return false; // Indicate failure
    }
  }, [store, propertyId, phoneNumber, isSuperkos]);

  const handleDeleteBroadcastChat = useCallback(() => {
    if (!broadcastIdToDelete) {
      return;
    }
    
    // Remove the broadcast list and its associated data
    store.whatsapp.removeBroadcastList(propertyId, broadcastIdToDelete);
    
    // Force immediate state update
    const updatedLists = store.whatsapp.getBroadcastLists(propertyId);
    setBroadcastLists([...updatedLists]); // Spread to ensure new array reference
    
    // If currently viewing this broadcast chat, clear it
    if (activeChat === `broadcast_${broadcastIdToDelete}`) {
      setActiveChat(null);
    }

    // Clear the deletion state
    setBroadcastIdToDelete(null);

    store.pushToastMessage?.({
      message: 'Broadcast chat berhasil dihapus',
      severity: 'success',
    });
  }, [store, propertyId, activeChat, broadcastIdToDelete, setBroadcastLists, setActiveChat, setBroadcastIdToDelete]);

  // ===== Connection Status =====
  useEffect(() => {
    const syncing = store.whatsapp.historySync?.syncing;
    const syncStatus = store.whatsapp.historySync?.status;
    
    if (syncing && (syncStatus === 'started' || status === "connecting")) {
      setShowConnecting(true);
    } else {
      setShowConnecting(false);
    }
  }, [store.whatsapp.historySync?.syncing, store.whatsapp.historySync?.status, status]);

  const handleAccountSwitch = useCallback((accountType) => {
    const toProperty = accountType === 'property';
    const toSuperkos = accountType === 'superkos';
    const canToggleFull = hasPermission && hasPermission(BAILEY_PERMISSION);
    const hasBasicWhatsapp = hasPermission && hasPermission(BASIC_WHATSAPP_PERMISSION);

    if (hasBasicWhatsapp && !canToggleFull && toProperty) {
      setPremiumModalOpen(true);
      return;
    }

    onChangeUsingFallback?.(toSuperkos);
    setActiveChat(null);
    setShowBanner(true);

  }, [store, hasPermission]);

  // ===== ADD CONFLICT HANDLING =====
  useEffect(() => {
    const status = getWaStatus(session?.status || 'disconnected');

    if (status.code === 'conflict') {
      openConflictDialog({
        onConfirm: async () => {
          await handleRefresh('conflict-resolution');
        }
      });
    }
  }, [session?.status, propertyId, handleRefresh, openConflictDialog]);

  // ===== Message Sending =====
  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending) return;

    // Handle broadcast chat
    if (store.whatsapp.isBroadcastChat(activeChat)) {
      setSending(true);
      try {
        const listId = activeChat.replace('broadcast_', '');
        const broadcastList = broadcastLists.find(list => list.id === listId);
        
        if (!broadcastList || !broadcastList.phones || broadcastList.phones.length === 0) {
          store.pushToastMessage?.({
            message: 'Broadcast list tidak ditemukan atau kosong.',
            severity: 'error',
          });
          return;
        }

        const payload = {
          recipients: broadcastList.phones,
          text,
          listId,
        };
        
        await store.whatsapp.sendBroadcastMessage(propertyId, payload);

        store.whatsapp.loadBroadcastMessagesIfNeeded(activeChat, propertyId, isSuperkos);

        setTimeout(() => {
          // Trigger a small state update to force re-render
          setDraft('');
          scrollToBottom(true);
        }, 100);

        store.pushToastMessage?.({
          message: `Pesan broadcast berhasil dikirim ke ${broadcastList.phones.length} nomor`,
          severity: 'success',
        });
        
        onAfterSend?.();
      } catch (err) {
        store.pushToastMessage?.({
          message: 'Gagal mengirim pesan broadcast. Silakan coba lagi.',
          severity: 'error',
        });
      } finally {
        setSending(false);
      }
      return;
    }

    const to = getRecipientNumber();
    if (!to) {
      store.pushToastMessage?.({
        message: 'Tidak ada nomor tujuan yang valid.',
        severity: 'error',
      });
      return;
    }

    setSending(true);
    try {
      const payload = {
        to,
        text,
        property_id: propertyId,
      };

      // Use original chatId for sending messages
      const { chatId } = parseCompositeId(activeChat);
      const targetChatId = chatId || activeChat;
      
      // Use new store method
      const data = await store.whatsapp.sendMessage(propertyId, payload, targetChatId);
      if (!data.success) {
        store.pushToastMessage?.({
          message: 'Periksa kembali internet anda atau coba refresh browser',
          severity: 'error',
        });
      }
      
      setDraft('');
      onAfterSend?.();
      setTimeout(() => scrollToBottom(true), 100);
      
    } catch (err) {
      store.pushToastMessage?.({
        message: 'Gagal mengirim pesan.',
        severity: 'error',
      });
    } finally {
      setSending(false);
    }
  }, [draft, sending, activeChat, getRecipientNumber, propertyId, store, onAfterSend, scrollToBottom, broadcastLists, isSuperkos]);

  const sendMedia = useCallback(async (file, caption = '') => {
    if (!file || sending) return;
    
    const to = getRecipientNumber();
    if (!to) {
      return;
    }

    const isImage = file.type?.startsWith('image/');
    const isPdf = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');

    if (!isImage && !isPdf) {
      store.pushToastMessage?.({
        message: 'Tipe file tidak didukung. Hanya gambar atau PDF yang diperbolehkan.',
        severity: 'error',
      });
      return;
    }

    setSending(true);
    try {
      const kind = file.type.startsWith('image/') ? 'image' :
                   file.type.startsWith('video/') ? 'video' :
                   file.type.startsWith('audio/') ? 'audio' : 'document';

      const fd = new FormData();
      fd.append('to', to);
      fd.append('type', kind);
      fd.append('propertyId', propertyId);
      if (caption) fd.append('caption', caption);
      fd.append('file', file);

      const mediaInfo = {
        type: kind,
        mimeType: file.type,
        caption: caption || '',
        fileName: file.name,
        size: file.size,
        localUrl: pendingMedia?.url
      };

      // Use original chatId for sending messages
      const { chatId } = parseCompositeId(activeChat);
      const targetChatId = chatId || activeChat;

      // Use new store method
      await store.whatsapp.sendMediaMessage(propertyId, fd, targetChatId, mediaInfo);
      onAfterSend?.();
    } catch (err) {
      // console.error('[sendMedia]', err?.response?.data || err.message);
    } finally {
      setSending(false);
    }
  }, [sending, getRecipientNumber, propertyId, activeChat, pendingMedia, store, onAfterSend]);

  // ===== Media Preview Handlers =====
  const openPreviewForFile = useCallback((file, initialCaption = '') => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const kind = file.type.startsWith('image/') ? 'image' :
                 file.type.startsWith('video/') ? 'video' :
                 file.type.startsWith('audio/') ? 'audio' : 'document';

    setPendingMedia({
      file,
      url,
      kind,
      name: file.name,
      size: file.size,
      caption: initialCaption,
    });
    setPreviewOpen(true);
  }, []);

  const closePreview = useCallback(() => {
    if (pendingMedia?.url) URL.revokeObjectURL(pendingMedia.url);
    setPendingMedia(null);
    setPreviewOpen(false);
  }, [pendingMedia]);

  const confirmSendMedia = useCallback(async () => {
    if (!pendingMedia) return;
    await sendMedia(pendingMedia.file, pendingMedia.caption || '');
    closePreview();
    setDraft('');
  }, [pendingMedia, sendMedia, closePreview]);

  // ===== Search Functions =====
  const findAllMatches = useCallback((keyword) => {
    if (!keyword || !messages?.length) return [];
    const q = String(keyword).trim().toLowerCase();
    const results = [];
    
    for (let idx = messages.length - 1; idx >= 0; idx--) {
      const m = messages[idx];
      const text = (m?.content?.text || m?.content?.caption || '').toString().toLowerCase();
      if (text && text.includes(q)) {
        const id = getMsgId(m, idx);
        results.push({ id, index: idx, message: m });
      }
    }
    return results;
  }, [messages, getMsgId]);

  const scrollToMatch = useCallback((result, behavior = 'smooth') => {
    if (!result) return;

    requestAnimationFrame(() => {
      const el = document.getElementById(`msg-${result.id}`);
      if (!el) return;

      const container = messagesRef.current;
      if (container?.contains(el)) {
        const elTop = el.getBoundingClientRect().top;
        const containerTop = container.getBoundingClientRect().top;
        const offset = elTop - containerTop + container.scrollTop - 
                      container.clientHeight / 2 + el.clientHeight / 2;
        container.scrollTo({ top: offset, behavior });
      } else {
        el.scrollIntoView({ behavior, block: 'center', inline: 'nearest' });
      }

      el.classList.add('wa-search-highlight');
      setTimeout(() => el.classList.remove('wa-search-highlight'), 1400);
    });
  }, []);

  const onSearchSubmit = useCallback((keyword) => {
    const matches = findAllMatches(keyword);
    setSearchResults(matches);
    setSearchIndex(0);
    setIsSearching(matches.length > 0);

    if (matches.length > 0 && keyword) {
      scrollToMatch(matches[0]);
    }
  }, [findAllMatches, scrollToMatch]);

  const gotoNext = useCallback(() => {
    if (!searchResults?.length) return;
    setSearchIndex(prev => {
      const next = (prev + 1) % searchResults.length;
      requestAnimationFrame(() => scrollToMatch(searchResults[next]));
      return next;
    });
  }, [searchResults, scrollToMatch]);

  const gotoPrev = useCallback(() => {
    if (!searchResults?.length) return;
    setSearchIndex(prev => {
      const prevIdx = (prev - 1 + searchResults.length) % searchResults.length;
      requestAnimationFrame(() => scrollToMatch(searchResults[prevIdx]));
      return prevIdx;
    });
  }, [searchResults, scrollToMatch]);

  // ===== Broadcast Handlers =====
  const handleSendBroadcast = useCallback(async ({ phones, text, listId }) => {
    setSending(true);
    try {
      const payload = {
        recipients: phones,
        text,
        listId,
      };
      
      await store.whatsapp.sendBroadcastMessage(propertyId, payload);

      store.pushToastMessage?.({
        message: `Pesan broadcast berhasil dikirim ke ${phones.length} nomor`,
        severity: 'success',
      });

      onAfterSend?.();
    } catch (err) {
      store.pushToastMessage?.({
        message: 'Gagal mengirim pesan broadcast. Silakan coba lagi.',
        severity: 'error',
      });
      throw err;
    } finally {
      setSending(false);
    }
  }, [store, propertyId, onAfterSend]);

  const handleSaveBroadcastList = useCallback((list) => {
    store.whatsapp.addBroadcastList(propertyId, list);
    setBroadcastLists(store.whatsapp.getBroadcastLists(propertyId));
  }, [store, propertyId]);

  const handleDeleteBroadcastList = useCallback((listId) => {
    store.whatsapp.removeBroadcastList(propertyId, listId);
    setBroadcastLists(store.whatsapp.getBroadcastLists(propertyId));
  }, [store, propertyId]);

  const handleUpdateBroadcastList = useCallback((updatedList) => {
    store.whatsapp.addBroadcastList(propertyId, updatedList);
    setBroadcastLists(store.whatsapp.getBroadcastLists(propertyId));
  }, [store, propertyId]);

  const handleSelectBroadcastChat = useCallback((broadcastList) => {
    setActiveChat(`broadcast_${broadcastList.id}`);
    setBroadcastOpen(false);
  }, []);

  // ===== Header Component =====
  const getChatHeader = () => {
    if (!selectedContact) {
      return (
        <Box>
          <Typography style={{ fontWeight: 700 }}>{t("Account")}</Typography>
          <Typography variant="caption" color="textSecondary">-</Typography>
        </Box>
      );
    }

    if (selectedContact.isBroadcast) {
      return (
        <Box>
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="space-between"
            style={{ cursor: 'pointer' }}
            onClick={() => setBroadcastDetailsOpen(true)}
          >
            <Box flex={1}>
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                {selectedContact.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Broadcast • {selectedContact.broadcastList?.phones?.length || 0} kontak • Klik untuk detail
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        <Box display="flex" alignItems="center" gap={0} flexWrap={'wrap'}>
          <Typography style={{ fontWeight: 700 }}>
            {selectedContact.name}
          </Typography>
        </Box>
        <Typography variant="caption" color="textSecondary" >
          {isSuperkos ? (
            <>
              Chat {selectedContact.phone} via Superkos • untuk properti &quot;{property?.name || '-'}&quot;
            </>
          ) : (
            <>
              {selectedContact.roomName && (
                <Chip 
                  size="small" 
                  label={selectedContact.roomName}
                  style={{ 
                    fontSize: 10, 
                    height: 20,
                    backgroundColor: '#e3f2fd',
                    color: '#0d47a1',
                    fontWeight: 'bold',
                    marginRight: 6
                  }}
                />
              )}
              {selectedContact.phone}
              <br />
              {selectedContact.isDuplicatePhone && (
                <Chip
                  size='small'
                  style={{
                    paddingLeft: 2,
                    paddingRight: 4,
                    fontSize: 10,
                    height: 18,
                    backgroundColor: '#ffecb3',
                    color: '#e65100'
                  }}
                  label={`⚠️ ${selectedContact.duplicateCount} tenants use this number`}
                />
              )}
            </>
          )}
        </Typography>
      </Box>
    );
  };

  // ===== Answered / Unanswered Actions =====
  const markSelectedChatAnswered = useCallback(async () => {
    if (!selectedContact || !selectedContact.chatId) return;
    try {
      await store.whatsapp.markAsAnsweredChat(propertyId, selectedContact.chatId);
      store.pushToastMessage?.({ message: 'Chat ditandai sebagai telah dijawab', severity: 'success' });
    } catch (err) {
      console.error('[markSelectedChatAnswered]', err);
      store.pushToastMessage?.({ message: 'Gagal menandai chat sebagai telah dijawab', severity: 'error' });
    }
  }, [selectedContact, propertyId, store]);

  const markSelectedChatUnanswered = useCallback(async () => {
    if (!selectedContact || !selectedContact.chatId) return;
    try {
      await store.whatsapp.markAsUnansweredChat(propertyId, selectedContact.chatId);
      store.pushToastMessage?.({ message: 'Chat ditandai sebagai belum terjawab', severity: 'success' });
    } catch (err) {
      console.error('[markSelectedChatUnanswered]', err);
      store.pushToastMessage?.({ message: 'Gagal menandai chat sebagai belum terjawab', severity: 'error' });
    }
  }, [selectedContact, propertyId, store]);

  // ===== Utility Functions =====
  const bytesToSize = (bytes) => {
    if (!Number(bytes)) return '-';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  // ===== Render =====
  const BroadcastDetailsDialog = () => {
    if (!selectedContact?.isBroadcast || !broadcastDetailsOpen) return null;

    const broadcastList = selectedContact.broadcastList;
    const phones = broadcastList?.phones || [];

    return (
      <Dialog 
        open={broadcastDetailsOpen} 
        onClose={() => setBroadcastDetailsOpen(false)}
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', padding: '16px 24px' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                {broadcastList?.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {phones.length} kontak dalam broadcast
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                setBroadcastIdToDelete(broadcastList.id);
                setBroadcastDetailsOpen(false);
                setBroadcastDeleteConfirmDialog(true);
              }}
              style={{ color: '#f44336' }}
            >
              <TrashIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent style={{ padding: 0 }}>
          {phones.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography variant="body2" color="textSecondary">
                Tidak ada kontak dalam broadcast ini
              </Typography>
            </Box>
          ) : (
            <List style={{ padding: 0 }}>
              {phones.map((phone, index) => {
                const name = phoneNameMap.get(phone) || phone;
                const contact = contacts.find(c => normalizePhone(c.phone) === phone);
                
                return (
                  <ListItem key={`${phone}-${index}`}>
                    <ListItemAvatar>
                      <Avatar style={{ 
                        backgroundColor: contact ? '#4caf50' : '#9e9e9e',
                        color: 'white'
                      }}>
                        {name.slice(0, 2).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography style={{ fontWeight: 500 }}>
                          {name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="textSecondary">
                          {phone}
                          {contact?.roomName && ` • ${contact.roomName}`}
                          {!contact && ' (Kontak tidak ditemukan)'}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>

        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={() => setBroadcastDetailsOpen(false)}>
            Tutup
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box className={classes.root}>
      <CssBaseline />
      <Grid container className={classes.shell} component={Paper} elevation={0}>
        {/* Sidebar desktop */}
        <Hidden smDown>
          <Grid item md={4}>
            <Box className={classes.sidebar}>
              {/* Header */}
              <Box className={classes.sideHeader}>
                <Box position="relative" display="inline-block">
                  <Avatar>
                    {(selectedProperty?.name || '??').slice(0, 2).toUpperCase()}
                  </Avatar>
                  {unansweredCount > 0 && (
                    <Chip
                      label={`${unansweredCount}`}
                      size="small"
                      style={{
                        position: 'absolute',
                        bottom: -6,
                        right: -6,
                        backgroundColor: '#fff3e0',
                        color: '#bf360c',
                        fontWeight: 700,
                        height: 20,
                        minWidth: 20,
                        padding: 0,
                        zIndex: 2,
                        borderRadius: 10,
                        fontSize: 12,
                        lineHeight: '20px',
                        textAlign: 'center'
                      }}
                      title={`${unansweredCount} unanswered chats for this property`}
                      aria-label={`${unansweredCount} unanswered chats`}
                    />
                  )}
                </Box>
                <Box ml={1} flex={1}>
                  <FormControl fullWidth className={classes.propertySelect}>
                    <InputLabel shrink htmlFor="prop-select">{t("Property")}</InputLabel>
                    <Select
                      displayEmpty
                      value={propertyId}
                      onChange={(e) => onSelectProperty?.(e.target.value)}
                      inputProps={{ id: 'prop-select' }}
                    >
                      {properties.map((p) => (
                        <MenuItem key={p._id} value={p._id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box mt={1} display="flex" alignItems="center" gap={8}>
                    {isSuperkos ? (
                      <Chip
                        label={t("SuperKos Account")}
                        size="small"
                        style={{
                          background: '#fff1',
                          color: '#0f172a',
                          border: '1px solid #eab308',
                          backgroundColor: '#fef3c7',
                          height: 24,
                          fontWeight: 700,
                        }}
                      />
                    ) : (
                      <WhatsAppStatusChip
                        session={session}
                        status={status}
                        propertyId={propertyId}
                        onSuccess={() => {}}
                      />
                    )}
                  </Box>
                </Box>

                {!isSuperkos && (
                  <OptionsMenu
                    onLogout={() => {
                      if (selectedProperty) {
                        store.whatsapp.stopSession?.(
                          propertyId,
                          phoneNumber
                        );
                      }
                    }}
                    onRefresh={() => handleRefresh('desktop')}
                    onBroadcast={() => setBroadcastOpen(true)}
                  />
                )}
              </Box>

              {/* Search */}
              <Box className={classes.searchWrap} display="flex" alignItems="center" gap={1}>
                <Box className={classes.search} style={{ flex: 1 }}>
                  <SearchIcon fontSize="small" />
                  <InputBase
                    placeholder={t("Search tenant, room, or phone")}
                    style={{ marginLeft: 8, width: '100%' }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Box>

                <IconButton size="small" onClick={(e) => setFilterAnchor(e.currentTarget)} style={{ marginLeft: 4 }}>
                  <FilterListIcon />
                </IconButton>

                <Popover
                  open={Boolean(filterAnchor)}
                  anchorEl={filterAnchor}
                  onClose={() => setFilterAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {/* use Box spacing props (px/py) instead of invalid CSS style paddingX */}
                  <Box px={4} py={2} minWidth={220}>
                    <FormControlLabel
                      style={{ width: '100%' }}
                      control={
                        <Switch
                          size="small"
                          color="primary"
                          checked={filterUnanswered}
                          onChange={(e) => setFilterUnanswered(e.target.checked)}
                        />
                      }
                      label={<Typography variant="body2">{t("Unanswered only")}</Typography>}
                    />
                    {/* future filters go here */}
                  </Box>
                </Popover>
              </Box>

              {/* Contacts */}
              <Box className={classes.contactsScroll}>
                {loadingChats || tenantsLoading ? (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Box textAlign="center">
                      <CircularProgress size={28} />
                      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                        Loading chats…
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <List>
                    {filteredContacts.map((c, i) => (
                      <React.Fragment key={ `${i}-${c.id}` }>
                        <ListItem 
                          button 
                          selected={c.id === activeChat} 
                          onClick={() => handleChatSelect(c)}
                          style={{
                            backgroundColor: c.status === 'unanswered' ? '#fffaf1ff' : undefined
                          }}
                        >
                          <ListItemAvatar>
                            <Badge
                              color="secondary"
                              badgeContent={c.unread}
                              overlap="circular"
                            >
                              <Avatar style={{
                                backgroundColor: c.isBroadcast ? '#128c7e' : undefined
                              }}>
                                {c.isBroadcast ? (
                                  <GroupIcon />
                                ) : (
                                  (c.name || '??').slice(0, 2).toUpperCase()
                                )}
                              </Avatar>
                            </Badge>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography style={{ fontWeight: 600 }}>
                                  {c.name}
                                </Typography>

                                {!c.isBroadcast && (
                                  <Chip 
                                    size='small'
                                    label={c.roomName}
                                    title={`Room: ${c.roomName}`}
                                    color="textSecondary" 
                                    style={{ 
                                      marginLeft: 4,
                                      fontSize: 9, 
                                      height: 18,
                                      backgroundColor: '#e3f2fd',
                                      color: '#0d47a1',
                                    }}
                                  />
                                )}

                                {c.isDuplicatePhone && (
                                  <TapTooltip
                                    title={`${c.duplicateCount} tenants share this phone number. Click for details.`}
                                  >
                                    <Chip 
                                      size="small" 
                                      label={`duplicate number`}
                                      title={`${c.duplicateCount} tenants share this phone number`}
                                      style={{ 
                                        marginLeft: 4,
                                        fontSize: 9, 
                                        height: 18,
                                        backgroundColor: '#ffecb3',
                                        color: '#e65100',
                                        fontWeight: 'bold'
                                      }} 
                                    />
                                  </TapTooltip>
                                )}
                                
                                {c.isBroadcast && (
                                  <Chip 
                                    size="small" 
                                    label="Broadcast" 
                                    style={{ 
                                      marginLeft: 4,
                                      fontSize: 10, 
                                      height: 16,
                                      backgroundColor: '#e8f5e8',
                                      color: '#2e7d2e'
                                    }} 
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">   
                                <Typography variant="body2" color="textSecondary" noWrap overflowY="hidden" style={{ maxWidth: '75%' }}>
                                  {c.fromMe && !c.isBroadcast && (
                                    <StatusIcon 
                                      status={c.lastMessageStatus || c.status} 
                                      isOptimistic={false}
                                    />
                                  )}
                                  {c.lastMessage || c.last}
                                </Typography>

                                {c.lastMessageAt && (
                                  <Typography variant="caption" color="textSecondary">
                                    {c.lastMessageAt}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            </Box>
          </Grid>
        </Hidden>

        
        {/* Mobile Drawer */}
        <Hidden mdUp implementation="js">
          <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
            <Box width={320} role="presentation" className={classes.sidebar}>
              {/* Header */}
              <Box className={classes.sideHeader}>
                <Box position="relative" display="inline-block">
                  <Avatar>
                    {(selectedProperty?.name || '??').slice(0, 2).toUpperCase()}
                  </Avatar>
                  {unansweredCount > 0 && (
                    <Chip
                      label={`${unansweredCount}`}
                      size="small"
                      style={{
                        position: 'absolute',
                        bottom: -6,
                        right: -6,
                        backgroundColor: '#fff3e0',
                        color: '#bf360c',
                        fontWeight: 700,
                        height: 20,
                        minWidth: 20,
                        padding: 0,
                        zIndex: 2,
                        borderRadius: 10,
                        fontSize: 12,
                        lineHeight: '20px',
                        textAlign: 'center'
                      }}
                      title={`${unansweredCount} unanswered chats for this property`}
                      aria-label={`${unansweredCount} unanswered chats`}
                    />
                  )}
                </Box>
                <Box ml={1} flex={1} width={10}>
                  <FormControl fullWidth className={classes.propertySelect}>
                    <InputLabel shrink htmlFor="prop-select-mobile">Property</InputLabel>
                    <Select
                      displayEmpty
                      value={propertyId}
                      onChange={(e) => {
                        onSelectProperty?.(e.target.value);
                        setDrawerOpen(false); // Close drawer after selection
                      }}
                      inputProps={{ id: 'prop-select-mobile' }}
                    >
                      {properties.map((p) => (
                        <MenuItem key={p._id} value={p._id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box mt={1} display="flex" alignItems="center" gap={8}>
                    {isSuperkos ? (
                      <Chip
                        label="Account Superkos"
                        size="small"
                        style={{
                          background: '#fff1',
                          color: '#0f172a',
                          border: '1px solid #eab308',
                          backgroundColor: '#fef3c7',
                          height: 24,
                          fontWeight: 700,
                        }}
                      />
                    ) : (
                      <WhatsAppStatusChip
                        session={session}
                        status={status}
                        propertyId={propertyId}
                        onSuccess={() => {}}
                      />
                    )}
                  </Box>
                </Box>

                {!isSuperkos && (
                  <OptionsMenu
                    onLogout={() => {
                      if (selectedProperty) {
                        store.whatsapp.stopSession?.(propertyId, phoneNumber);
                      }
                      setDrawerOpen(false);
                    }}
                    onRefresh={async () => {
                      const success = await handleRefresh('mobile');
                      setDrawerOpen(false); // Close drawer regardless of success/failure
                    }}
                    onBroadcast={() => {
                      setBroadcastOpen(true);
                      setDrawerOpen(false);
                    }}
                  />
                )}
              </Box>

              {/* Search */}
              <Box className={classes.searchWrap}>
                <Box className={classes.search}>
                  <SearchIcon fontSize="small" />
                  <InputBase
                    placeholder="Search tenant, room, or phone"
                    style={{ marginLeft: 8, width: '100%' }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Box>
              </Box>

              {/* Contacts */}
              <Box className={classes.contactsScroll}>
                {loadingChats || tenantsLoading ? (
                  <Box display="flex" alignItems="center" justifyContent="center" height="200px">
                    <Box textAlign="center">
                      <CircularProgress size={28} />
                      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                        Loading chats…
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <List>
                    {filteredContacts.map((c, i) => (
                      <React.Fragment key={`mobile-${i}-${c.id}`}>
                        <ListItem 
                          button 
                          selected={c.id === activeChat} 
                          onClick={() => {
                            handleChatSelect(c);
                            setDrawerOpen(false); // Close drawer after selection
                          }}
                          style={{
                            backgroundColor: c.status === 'unanswered' ? '#fffaf1ff' : undefined
                          }}
                        >
                          <ListItemAvatar>
                            <Badge
                              color="secondary"
                              badgeContent={c.unread}
                              overlap="circular"
                            >
                              <Avatar style={{
                                backgroundColor: c.isBroadcast ? '#128c7e' : undefined
                              }}>
                                {c.isBroadcast ? (
                                  <GroupIcon />
                                ) : (
                                  (c.name || '??').slice(0, 2).toUpperCase()
                                )}
                              </Avatar>
                            </Badge>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography style={{ fontWeight: 600, fontSize: 14 }}>
                                  {c.name}
                                </Typography>

                                {!c.isBroadcast && (
                                  <Chip 
                                    size='small'
                                    label={c.roomName}
                                    style={{ 
                                      marginLeft: 4,
                                      fontSize: 8, 
                                      height: 16,
                                      backgroundColor: '#e3f2fd',
                                      color: '#0d47a1',
                                    }}
                                  />
                                )}

                                {c.isDuplicatePhone && (
                                  <Chip 
                                    size="small" 
                                    label={`duplicate`}
                                    style={{ 
                                      marginLeft: 4,
                                      fontSize: 8, 
                                      height: 16,
                                      backgroundColor: '#ffecb3',
                                      color: '#e65100',
                                      fontWeight: 'bold'
                                    }} 
                                  />
                                )}
                                
                                {c.isBroadcast && (
                                  <Chip 
                                    size="small" 
                                    label="Broadcast" 
                                    style={{ 
                                      marginLeft: 4,
                                      fontSize: 8, 
                                      height: 16,
                                      backgroundColor: '#e8f5e8',
                                      color: '#2e7d2e'
                                    }} 
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">   
                                <Typography variant="body2" color="textSecondary" noWrap overflowY="hidden" style={{ maxWidth: '75%', fontSize: 12 }}>
                                  {c.fromMe && !c.isBroadcast && (
                                    <StatusIcon 
                                      status={c.lastMessageStatus || c.status} 
                                      isOptimistic={false}
                                    />
                                  )}
                                  {c.lastMessage || c.last}
                                </Typography>

                                {c.lastMessageAt && (
                                  <Typography variant="caption" color="textSecondary" style={{ fontSize: 10 }}>
                                    {c.lastMessageAt}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            </Box>
          </Drawer>
        </Hidden>

        {/* Chat area */}
        <Grid item xs={12} md={8}>
          <Box className={classes.chatWrap}>
            {/* Header */}
            <AppBar position="static" elevation={0} className={classes.chatHeader}>
              <Toolbar style={{ paddingLeft: 8, paddingRight: 8 }}>
                <Hidden mdUp>
                  <IconButton 
                    edge="start" 
                    onClick={() => setDrawerOpen(true)}
                    style={{ marginRight: 8 }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Hidden>
                
                <Box className={classes.titleRow} style={{ flex: 1, minWidth: 0 }}>
                  <Avatar style={{
                    backgroundColor: selectedContact?.isBroadcast ? '#128c7e' : undefined,
                    width: 32,
                    height: 32 // Smaller on mobile
                  }}>
                    {selectedContact?.isBroadcast ? (
                      <GroupIcon />
                    ) : (
                      (selectedContact?.name || 'SK').slice(0, 2).toUpperCase()
                    )}
                  </Avatar>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    {getChatHeader()}
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                  {/* Simplified mobile controls */}
                  <Hidden smDown>
                    {/* Full controls for larger screens */}
                    {selectedContact && !selectedContact.isBroadcast && (
                      <Button
                        size="small"
                        variant='outlined'
                        onClick={() => {
                          if (selectedContact.status === 'unanswered') {
                            markSelectedChatAnswered();
                          } else {
                            markSelectedChatUnanswered();
                          }
                        }}
                      >
                        {selectedContact.status === 'unanswered' ? 'Mark as answered' : 'Mark as unanswered'}
                      </Button>
                    )}
                    <Box className={classes.searchMessage}>
                      <SearchIcon fontSize="small" />
                      <InputBase
                        placeholder="Cari pesan apa?"
                        style={{ marginLeft: 8, flex: 1, minWidth: 120, maxWidth: 200 }}
                        value={searchMessage}
                        onChange={(e) => {
                          setSearchMessage(e.target.value);
                          if (searchTimeout.current) clearTimeout(searchTimeout.current);
                          searchTimeout.current = setTimeout(() => onSearchSubmit(e.target.value), 400);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (searchTimeout.current) clearTimeout(searchTimeout.current);
                            onSearchSubmit(searchMessage);
                          } else if (e.key === 'Escape' || searchMessage === "") {
                            setSearchMessage('');
                            setSearchResults([]);
                            setIsSearching(false);
                          }
                        }}
                      />
                      <Box display="flex" flexDirection="row" alignItems="center" style={{ gap: 4 }}>
                        <IconButton size="small" onClick={gotoNext} disabled={!searchResults.length}>
                          <ChevronUp size={14} />
                        </IconButton>
                        <Typography variant="caption">
                          {searchResults.length ? `${searchIndex + 1}/${searchResults.length}` : '0'}
                        </Typography>
                        <IconButton size="small" onClick={gotoPrev} disabled={!searchResults.length}>
                          <ChevronDown size={14} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Hidden>
                  <Hidden mdUp>
                    {/* Account Switch - Smaller on mobile */}
                    { hasPermission(BASIC_WHATSAPP_PERMISSION) && (
                      <FormControl variant="outlined" size="small">
                        <Select
                          value={isSuperkos ? 'superkos' : 'property'}
                          onChange={(e) => {
                            handleAccountSwitch(e.target.value);
                          }}
                          style={{ 
                            height: 32,
                            minWidth: 100, // Smaller on mobile
                            fontSize: 12
                          }}
                        >
                          <MenuItem value="property">{t("Property")}</MenuItem>
                          <MenuItem value="superkos">{t("SuperKos")}</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Hidden>
                </Box>
              </Toolbar>
            </AppBar>

            {/* Fallback Banner */}
            {showBanner && isSuperkos && hasPermission(BAILEY_PERMISSION) && (
              <Box className={classes.banner} position={"relative"}>
                <Box flex={1} paddingRight={2}>
                  {status?.code !== 'connected' ? (
                    <>
                      Pesan terkirim melalui nomor WhatsApp <b>Superkos</b>.
                      Untuk menggunakan WhatsApp properti <b>{property?.name}</b>, silakan{' '} 
                      <a href="#" onClick={onOpenScan}><b>sambungkan</b></a> dahulu.
                    </>
                  ) : (
                    <>
                      Percakapan ini ditampilkan melalui akun <b>Superkos</b>. 
                      Untuk beralih kembali ke akun WhatsApp milik properti <b>{property?.name}</b>, klik{' '}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          onChangeUsingFallback?.(false);
                          setActiveChat(null);
                        }}
                      >
                        <b>disini</b>
                      </a>
                    </>
                  )}
                </Box>
                <IconButton 
                  size="small" 
                  onClick={() => setShowBanner(false)}
                  style={{ 
                    position: 'absolute', 
                    top: 4, 
                    right: 4,
                    marginLeft: 8,
                    color: '#7a5b00',
                    padding: 4
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            )}
            {showBanner && !isSuperkos && status?.code !== 'connected' && hasPermission(BAILEY_PERMISSION) && (
              <Box className={classes.banner} position={"relative"}>
                <Box flex={1}>
                  {t("WhatsApp for property {{propertyName}} is not connected.", { propertyName: property?.name })}{' '}
                  <a href="#" onClick={onOpenScan}>
                    <b>{t("Connect")}</b>
                  </a>
                </Box>
                <IconButton 
                  size="small" 
                  onClick={() => setShowBanner(false)}
                  style={{ 
                    position: 'absolute', 
                    top: 4, 
                    right: 4,
                    marginLeft: 8,
                    color: '#7a5b00',
                    padding: 4
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            )}

            {selectedContact?.name ? (
              <>
                {/* Messages */}
                <Box className={classes.messages} ref={messagesRef}>
                  {loadingChats ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Box textAlign="center">
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                          Loading conversation…
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      {sortedDays.map((day) => (
                        <React.Fragment key={day}>
                          <Box display="flex" justifyContent="center">
                            <DayLabel
                              label={
                                day === new Date().toISOString().slice(0, 10)
                                  ? 'Today'
                                  : day === new Date(Date.now() - 86400000).toISOString().slice(0, 10)
                                  ? 'Yesterday'
                                  : new Intl.DateTimeFormat('en-US', { 
                                      weekday: 'long', 
                                      year: 'numeric', 
                                      month: 'long', 
                                      day: 'numeric' 
                                    }).format(new Date(day))
                              }
                            />
                          </Box>

                          {(perDay[day] || []).map((message) => {
                            const globalIdx = messages.indexOf(message);
                            return (
                              <MessageBubble
                                key={getMsgId(message, globalIdx)}
                                message={message}
                                index={globalIdx}
                                fmtTimeID={fmtTimeID}
                                getMsgId={getMsgId}
                              />
                            );
                          })}
                        </React.Fragment>
                      ))}
                      <div ref={msgEndRef} />
                    </>
                  )}
                </Box>

                {/* Input */}

                { hasPermission(BAILEY_PERMISSION) && (
                  <Box className={classes.inputBar}>
                    <Box>
                      <IconButton 
                        disabled={isSuperkos || loadingChats || sending} 
                        onClick={toggleEmojiPicker}
                      >
                        <EmojiEmotionsOutlinedIcon />
                      </IconButton>
                      
                      {emojiPickerOpen && (
                        <Box position="absolute" bottom={60} left={16} zIndex={1000}>
                          <EmojiPicker 
                            onEmojiClick={(emojiObject, event) => {
                              setDraft((d) => d + emojiObject.emoji);
                              setEmojiPickerOpen(false);
                            }}
                            disableAutoFocus={true}
                            native
                          />
                        </Box>
                      )}
                      
                      <IconButton 
                        disabled={isSuperkos || loadingChats || sending} 
                        onClick={onPickFile}
                      >
                        <AttachFileIcon />
                      </IconButton>
                    </Box>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*,audio/*,application/pdf"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          openPreviewForFile(file, draft.trim());
                        }
                        e.target.value = '';
                      }}
                    />
                    
                    <Paper className={classes.input} elevation={0}>
                      <InputBase
                        multiline
                        maxRows={4}
                        placeholder={isSuperkos ? 'Read only (Superkos)' : 'Type a message'}
                        fullWidth
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (!isSuperkos && !loadingChats && e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            send();
                          }
                        }}
                        disabled={isSuperkos || loadingChats}
                      />
                    </Paper>

                    <IconButton
                      color="primary"
                      onClick={send}
                      disabled={isSuperkos || loadingChats || !draft.trim() || sending}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                )}
              </>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                textAlign="center"
                p={4}
                sx={{
                  backgroundColor: '#f0f2f5',
                  color: '#121212',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Tidak ada percakapan yang dipilih
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 360 }}>
                  Pilih salah satu kontak di sebelah kiri untuk mulai mengirim dan menerima pesan.
                  Semua percakapan Anda akan tampil di sini.
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Media Preview Dialog */}
      <Dialog open={previewOpen} onClose={closePreview} fullWidth maxWidth="sm">
        <DialogTitle>{t("Preview")}</DialogTitle>
        <DialogContent>
          {pendingMedia?.kind === 'image' && (
            <img src={pendingMedia.url} alt={pendingMedia.name} className={classes.previewMedia} />
          )}
          {pendingMedia?.kind === 'video' && (
            <video src={pendingMedia.url} controls className={classes.previewMedia} />
          )}
          {pendingMedia?.kind === 'audio' && (
            <audio src={pendingMedia.url} controls style={{ width: '100%' }} />
          )}
          {pendingMedia?.kind === 'document' && (
            <Box p={2} borderRadius={8} bgcolor="#f3f4f6">
              <Typography variant="subtitle2">{pendingMedia?.name}</Typography>
              <Typography variant="body2" color="textSecondary">
                {bytesToSize(pendingMedia?.size)}
              </Typography>
            </Box>
          )}

          <Box mt={2}>
            <TextField
              label={t("Caption (optional)")}
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              value={pendingMedia?.caption || ''}
              onChange={(e) => setPendingMedia(pm => ({ ...pm, caption: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview} disabled={sending}>{t("Cancel")}</Button>
          <Button 
            onClick={confirmSendMedia} 
            color="primary" 
            variant="contained" 
            startIcon={<SendIcon />} 
            disabled={sending || isSuperkos}
          >
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Broadcast Dialog */}
      <BroadcastDialog
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        contacts={contacts}
        property={property}
        onSendBroadcast={handleSendBroadcast}
        sending={sending}
        isSuperkos={isSuperkos}
        broadcastLists={broadcastLists}
        onSaveBroadcastList={handleSaveBroadcastList}
        onDeleteBroadcastList={handleDeleteBroadcastList}
        onUpdateBroadcastList={handleUpdateBroadcastList}
        onSelectBroadcastChat={handleSelectBroadcastChat}
      />

      <BroadcastDeleteConfirmDialog
        title="Hapus Broadcast List"
        subTitle="Apakah Anda yakin ingin menghapus broadcast list ini? Ini tidak akan menghapus percakapan yang sudah ada."
        onConfirm={handleDeleteBroadcastChat}
        onClose={() => setBroadcastIdToDelete(null)}
      />

      <BroadcastDetailsDialog />

      {/* Connection Status Toast */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showConnecting}
      >
        <SnackbarContent
          className={classes.connectingToast}
          message={
            <Box>
              <div className={classes.connectingTitle}>Menyinkronkan WhatsApp…</div>
              <div className={classes.connectingSub}>
                {`Nomer: ${store.whatsapp.historySync?.phoneNumber || '-'}`}
              </div>
              <LinearProgress />
            </Box>
          }
        />
      </Snackbar>

      {/* Conflict Dialog */}
      <ConflictDialog />

      {/* Premium Notice Modal */}
      <PremiumNoticeModal open={premiumModalOpen} onClose={() => setPremiumModalOpen(false)} onUpgrade={() => setPremiumModalOpen(false)} />
      
    </Box>
  );
});
